import { Command, Option } from "clipanion";
import { M8tCommand } from "../../lib/m8t-command.js";
import { LocalCliError } from "../../lib/errors.js";
import { colors } from "../../lib/output.js";
import { readBootstrapState } from "../../lib/bootstrap-state.js";
import { readStatusBlob } from "../../lib/bootstrap-status.js";
import { reapInstaller, sweepOrphanOwnerAssignments, sweepOrphanGatewaySubRoles } from "../../lib/bootstrap-reap.js";
import { getAciState } from "../../lib/bootstrap-aci-state.js";
import { getAzAccount } from "../../lib/auth.js";

export class BootstrapReapCommand extends M8tCommand {
  static paths = [["bootstrap", "reap"]];
  static usage = Command.Usage({
    description: "Tear down the installer scaffolding (ACI → MI → its role assignments) after a successful install.",
    details:
      "Runs locally on the 'done' signal (the installer can't delete its own identity). The platform RG and the gateway's own assignments persist. A failed install is left intact for diagnosis unless --force.\n\n" +
      "--sweep-orphans is a DIFFERENT and much broader mode: instead of reaping this install, it scans the WHOLE SUBSCRIPTION for m8t role assignments left behind by installs whose resources are gone — orphaned installer Owner-at-subscription-scope grants and orphaned gateway subscription-scope roles. It is a dry run that only lists what it found unless you also pass --yes, which deletes them.",
    examples: [
      ["Reap after done", "$0 bootstrap reap"],
      ["Reap a failed install anyway", "$0 bootstrap reap --force"],
      ["List orphaned role assignments across the subscription", "$0 bootstrap reap --sweep-orphans"],
      ["…and delete them", "$0 bootstrap reap --sweep-orphans --yes"],
    ],
  });

  force = Option.Boolean("--force", false, { description: "Reap even if the install failed or never reported a status." });
  sweepOrphans = Option.Boolean("--sweep-orphans", false, { description: "Subscription-wide: find m8t role assignments orphaned by earlier installs. Lists only, unless --yes." });
  yes = Option.Boolean("--yes", false, { description: "With --sweep-orphans, actually delete what the sweep found." });

  async executeCommand(): Promise<number> {
    if (this.sweepOrphans === true) {
      const { subscriptionId: sub } = await getAzAccount();
      if (!sub) {
        throw new LocalCliError({ code: "BOOTSTRAP_SWEEP_NO_SUB", message: "No active subscription.", hint: "Run 'az account set --subscription <id>'." });
      }
      const execute = this.yes === true;
      const owner = await sweepOrphanOwnerAssignments({ subscriptionId: sub, execute });
      const gateway = await sweepOrphanGatewaySubRoles({ subscriptionId: sub, execute });
      const total = owner.length + gateway.length;
      if (total === 0) {
        this.context.stdout.write(`${colors.success("✓")} No orphaned installer Owner@sub or gateway sub-scope assignments found.\n`);
        return 0;
      }
      for (const f of owner) {
        this.context.stdout.write(`  ${colors.dim("Owner@sub")}  ${f.id}${f.createdOn ? colors.dim(` (created ${f.createdOn})`) : ""}${f.description ? colors.dim(" [tagged]") : ""}\n`);
      }
      for (const f of gateway) {
        this.context.stdout.write(`  ${colors.dim(f.roleDefinitionName ?? "gateway sub-scope")}  ${f.id}${f.createdOn ? colors.dim(` (created ${f.createdOn})`) : ""}${f.description ? colors.dim(" [tagged]") : ""}\n`);
      }
      const breakdown = `${String(owner.length)} Owner@sub, ${String(gateway.length)} gateway sub-scope`;
      if (execute) {
        this.context.stdout.write(`${colors.success("✓")} Removed ${String(total)} orphaned assignment(s) (${breakdown}).\n`);
      } else {
        this.context.stdout.write(`\nFound ${String(total)} orphaned assignment(s) (${breakdown}) (dry-run). ${colors.hint("Re-run with --sweep-orphans --yes to delete them.")}\n`);
      }
      return 0;
    }

    const state = await readBootstrapState();
    if (!state) {
      // A launch that failed BEFORE writing state (e.g. an inaccessible installer
      // image right after the MI + Owner@sub grant) leaves no local record but may
      // have orphaned that privileged grant. Without --force we can't know there's
      // anything to clean, so --force self-heals by sweeping the tagged orphans.
      if (this.force === true) {
        const { subscriptionId: sub } = await getAzAccount();
        if (!sub) {
          throw new LocalCliError({ code: "BOOTSTRAP_SWEEP_NO_SUB", message: "No active subscription.", hint: "Run 'az account set --subscription <id>'." });
        }
        const owner = await sweepOrphanOwnerAssignments({ subscriptionId: sub, execute: true });
        const gateway = await sweepOrphanGatewaySubRoles({ subscriptionId: sub, execute: true });
        const total = owner.length + gateway.length;
        this.context.stdout.write(
          total === 0
            ? `${colors.success("✓")} No bootstrap state and no orphaned installer Owner@sub or gateway sub-scope assignments. Nothing to reap.\n`
            : `${colors.success("✓")} No bootstrap state found; swept ${String(total)} orphaned assignment(s) (${String(owner.length)} Owner@sub, ${String(gateway.length)} gateway sub-scope).\n`,
        );
        return 0;
      }
      throw new LocalCliError({
        code: "BOOTSTRAP_NO_STATE",
        message: "No bootstrap state to reap.",
        hint: "Nothing to do. If a launch failed and its RG was deleted it may have orphaned an installer Owner@sub grant or the gateway's sub-scope reader roles — run 'm8t bootstrap reap --force' (or 'm8t bootstrap reap --sweep-orphans --yes') to clean them.",
      });
    }
    // With --force we tear down regardless of install status AND without requiring a
    // readable status blob — an early-failed install may never have created the status
    // storage account, and reading it would throw before we could reap. Only gate on
    // status when NOT forcing.
    if (this.force !== true) {
      const doc = await readStatusBlob({ saName: state.statusSaName, resourceGroup: state.resourceGroup, subscriptionId: state.subscriptionId });
      if (doc.status !== "done") {
        if (doc.status === "failed") {
          throw new LocalCliError({
            code: "BOOTSTRAP_REAP_FAILED_INSTALL",
            message: `The install failed at '${doc.error?.phase ?? doc.phase}'. Leaving the installer up for diagnosis.`,
            hint: `Inspect: az container logs -n ${state.aciName} -g ${state.resourceGroup}. Reap anyway with --force.`,
          });
        }
        const aci = await getAciState({ aciName: state.aciName, resourceGroup: state.resourceGroup, subscriptionId: state.subscriptionId }).catch(() => null);
        const deadHint = aci?.terminated
          ? `The installer container has terminated (exit ${String(aci.exitCode ?? "?")}) without reaching done — reap with --force.`
          : `Wait for 'm8t bootstrap status --watch' to reach done, or reap with --force if the install is stuck.`;
        throw new LocalCliError({
          code: "BOOTSTRAP_REAP_NOT_DONE",
          message: `The install is not done yet (status: ${doc.status}, phase: ${doc.phase}).`,
          hint: deadHint,
        });
      }
    }
    await reapInstaller({
      subscriptionId: state.subscriptionId, resourceGroup: state.resourceGroup,
      aciName: state.aciName, miName: state.miName, roleAssignmentIds: state.roleAssignmentIds,
      principalId: state.miPrincipalId,
    });
    this.context.stdout.write(`${colors.success("✓")} reaped the installer (ACI + identity + its role assignments). The platform stays.\n`);
    this.context.stdout.write(`  ${colors.hint("next:")} m8t open   ${colors.dim("# your local setup was completed by 'm8t bootstrap status --watch'")}\n`);
    return 0;
  }
}
